"use client";

import { useRouter } from "next/navigation";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useTransition,
} from "react";

/**
 * Switch between transition modes:
 * - "dim":      Keep old content visible at reduced opacity while new data loads (Version A)
 * - "skeleton": Unmount old content immediately, show loading.tsx skeleton (Version B)
 */
export type TransitionMode = "dim" | "skeleton";

const TRANSITION_MODE: TransitionMode = "dim";

interface NavigationTransitionContextValue {
  isPending: boolean;
  navigateWithTransition: (url: string) => void;
  transitionMode: TransitionMode;
}

const NavigationTransitionContext =
  createContext<NavigationTransitionContextValue>({
    isPending: false,
    navigateWithTransition: () => {
      // no-op default
    },
    transitionMode: TRANSITION_MODE,
  });

export function useNavigationTransition() {
  return useContext(NavigationTransitionContext);
}

export function NavigationTransitionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const navigateWithTransition = useCallback(
    (url: string) => {
      if (TRANSITION_MODE === "dim") {
        // Wrap in startTransition so React keeps the old UI while loading
        startTransition(() => {
          router.push(url);
        });
      } else {
        // Regular push — triggers loading.tsx (skeleton) immediately
        router.push(url);
      }
    },
    [router]
  );

  return (
    <NavigationTransitionContext.Provider
      value={{
        isPending: TRANSITION_MODE === "dim" ? isPending : false,
        navigateWithTransition,
        transitionMode: TRANSITION_MODE,
      }}
    >
      {children}
    </NavigationTransitionContext.Provider>
  );
}
